import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { ApprovalStatus, ContractStatus } from '@/services/api/contracts/companies';
import { useCompany, useUpdateCompany } from '@/services/api/hooks/companies';
import { zodResolver } from '@hookform/resolvers/zod';

import AddressField from '@/components/form/fields/address/AddressField';
import { ToggleInput } from '@/components/form/fields/toggle/Toggle';
import { deserialiseAddress, serialiseAddress } from '@/pages/onboarding/commonSteps/addressHelper';
import { Address } from '@/services/api/commonTypes';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { FunctionComponent, Suspense, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import EmployeeCountField from '../EmployeeCountField';
import { getCustomerStatus } from './helper';

const EditCustomerFormFields = z.object({
  companyInfo: z.object({
    name: z.string().min(1, 'Required'),
    number: z.string().min(1, 'Required'),
    address: Address,
    employeeCount: z.number().min(1),
    vatNumber: z.string().min(1, 'Required')
  }),
  status: z.custom<Property>(),
  enableCustomNotifications: z.boolean(),
  isProductToursEnabled: z.boolean(),
  haloClientId: z.string().optional().nullable(),
  haloGeneralUserId: z.string().optional().nullable(),
  addHaloStockSites: z.string().optional().nullable()
});

type EditCustomerFormFields = z.infer<typeof EditCustomerFormFields>;

const getStatus = (key: string) => {
  return ApprovalStatus[key as keyof typeof ApprovalStatus];
};

const getStatusOptions = (contractStatus: ContractStatus) => {
  const values = Object.keys(ApprovalStatus)
    .filter(
      key =>
        !isNaN(Number(getStatus(key))) &&
        (contractStatus > ContractStatus.Pending || getStatus(key) < ApprovalStatus.Not)
    )
    .map(key => {
      const value = ApprovalStatus[key as keyof typeof ApprovalStatus];
      return {
        id: String(value),
        name: getCustomerStatus(value)
      };
    });

  return values;
};



const EditCustomerModal: FunctionComponent<ModalProps<{ mspId: string; customerId: string }>> = props => {
  const { role } = useUser();
  const EditCustomer = () => {
    const mspId = props.props?.mspId;
    const customerId = props.props?.customerId;
    if (!mspId) throw new Error('Missing partner id');
    if (!customerId) throw new Error('Missing company id');

    const [customer] = useCompany(mspId, customerId);
    const updateCustomer = useUpdateCompany(mspId);

    const methods = useForm<EditCustomerFormFields>({
      resolver: zodResolver(EditCustomerFormFields),
      defaultValues: {
        companyInfo: {
          name: customer.registeredInfo.name,
          number: customer.registeredInfo?.number || '',
          address: deserialiseAddress(customer.registeredInfo?.address),
          employeeCount: customer.registeredInfo?.employeeCount ?? 1,
          vatNumber: customer.registeredInfo.vatNumber
        },
        status: {
          id: String(customer.approvalStatus ?? ApprovalStatus.Pending),
          name: getCustomerStatus(customer.approvalStatus ?? ApprovalStatus.Pending)
        },
        enableCustomNotifications: customer.enableCustomNotifications ?? false,
        isProductToursEnabled: customer.isProductToursEnabled ?? false,
        haloClientId: customer.haloClientId || '',
        haloGeneralUserId: customer.haloGeneralUserId || '',
        addHaloStockSites: customer.addHaloStockSites?.join(', ') || ''
      }
    });

    const onSubmit = async (data: EditCustomerFormFields) => {
      const result = await updateCustomer.mutateAsync({
        id: customer.id,
        name: data.companyInfo.name,
        code: customer.code,
        registeredInfo: {
          name: data.companyInfo.name,
          number: data.companyInfo.number,
          address: serialiseAddress(data.companyInfo.address),
          employeeCount: data.companyInfo.employeeCount,
          vatNumber: data.companyInfo.vatNumber
        },
        disabled: customer.disabled,
        approvalStatus: isNaN(Number(data.status.id)) ? ApprovalStatus.Pending : (Number(data.status.id) as ApprovalStatus),
        selectedProductIds: customer.propertyBag.selectedProductIds || [],
        enableCustomNotifications: data.enableCustomNotifications,
        isProductToursEnabled: data.isProductToursEnabled,
        haloClientId: data.haloClientId || null,
        haloGeneralUserId: data.haloGeneralUserId || null,
        addHaloStockSites: data.addHaloStockSites ? data.addHaloStockSites.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n)) : null
      });

      if (result.id) {
        props.close();
      }
    };

    const errorObj = updateCustomer.error as Error | undefined;
    const error = errorObj ? errorObj.message : '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Edit Customer</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col space-y-4 py-3" methods={methods} onSubmit={onSubmit}>
          {role.type === RoleType.Admin && (
            <SelectField id="status" label="Status" options={getStatusOptions(customer.contractStatus)} />
          )}

          <div>
            <p className="font-semibold text-lg">Business information</p>
            <div className="flex flex-row space-x-5">
              <TextField className="flex-1" id="companyInfo.name" label="Company name" />
              <TextField id="companyInfo.number" label="Registered company number" />
            </div>
            <TextField id="companyInfo.vatNumber" label="VAT number" />
            <AddressField fieldKey="companyInfo" />
            <EmployeeCountField />
          </div>

          {role.type === RoleType.Admin && (
            <>
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium text-gray-700">Enable Custom Notifications</p>
                <Controller
                  name="enableCustomNotifications"
                  control={methods.control}
                  render={({ field }) => (
                    <ToggleInput id="enableCustomNotifications" value={field.value} onChange={field.onChange} />
                  )}
                />
              </div>
              <div className="flex items-center gap-3 mt-2">
                <p className="text-sm font-medium text-gray-700">Enable Product Tours</p>
                <Controller
                  name="isProductToursEnabled"
                  control={methods.control}
                  render={({ field }) => (
                    <ToggleInput id="isProductToursEnabled" value={field.value} onChange={field.onChange} />
                  )}
                />
              </div>
              <div className="flex flex-row space-x-5 mt-4">
                <TextField className="flex-1" id="haloClientId" label="Halo Client ID" />
                <TextField className="flex-1" id="haloGeneralUserId" label="Halo General User ID" />
              </div>
              <div className="flex flex-row space-x-5 mt-4">
                <TextField className="flex-1" id="addHaloStockSites" label="Halo Stock Sites (comma-separated IDs)" />
              </div>
            </>
          )}

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex justify-end pt-2">
            <Button loading={updateCustomer.isLoading} className="w-fit" variant={ButtonVariant.Primary} type="submit">
              Update
            </Button>
          </div>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <EditCustomer />
      </Suspense>
    </Modal>
  );
};

export default EditCustomerModal;
